import { faCheck, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { Node, Handle, NodeProps, Position } from "@xyflow/react";
import { FunctionComponent, useMemo } from "react";

import { Icon } from "@/components";

import { InputField } from "@/components/InputField/InputField";
import { ButtonSize } from "@/components/Button/types";

import { classNames } from "@/utils/classNames";

import { Information } from "@/components/Information/Information";

import { NodeData } from "../../pages/CampaignPage";
import { StepFailureNote } from "./StepFailureNote";

export type LinkedinRemoveInviteNodeConfig = Node<NodeData, "removeLinkedinVisitNode">;

export const LinkedinRemoveInviteNode: FunctionComponent<
	NodeProps<LinkedinRemoveInviteNodeConfig>
> = (props) => {
	const { data } = props;

	const previousNode = data.getPreviousNode?.(props.id);
	const childrenNodes = data.getChildrenNodes?.(props.id) || [];

	const currentType = useMemo(() => {
		return previousNode?.type || "starterNode";
	}, [previousNode]);

	const hasNextNode = useMemo(() => {
		return childrenNodes.length >= 1 || false;
	}, [childrenNodes]);

	return (
		<div
			className={classNames(
				"rounded-lg border h-auto relative w-[350px]",
				data.isDisconnectedTarget ? "border-status-error border-2" : "border-gray-200",
				data.failed && !data.isDisconnectedTarget ? "border-status-error" : "",
				data.alreadySent ? "bg-brand-white" : "bg-white"
			)}
		>
			<div className="text-xs h-auto w-full bg-action-200 text-text-meta px-2  py-1 rounded-t-lg flex items-center gap-1">
				<span className="">Step {data.level} - Remove LinkedIn invite</span>
				{currentType !== "starterNode" && !data.alreadySent ? (
					<>
						<span className="text-text-meta">after</span>{" "}
						<div className="w-[60px]">
							<InputField
								className="-mb-3"
								handleChange={(e) => {
									if (
										isNaN(Number(e.target.value)) ||
										e.target.value?.length > 2 ||
										Number(e.target.value) < 0 ||
										Number(e.target.value) > 30
									) {
										return;
									}

									data?.handleSetDays?.(props.id, Number(e.target.value));
								}}
								max={30}
								min={0}
								name="days"
								pattern="[0-9]{2}"
								placeholder="days"
								size={ButtonSize.XS}
								type="text"
								value={data.days || 0}
							/>
						</div>
						<span className="text-text-meta">
							{(data.days || 0) > 1 || !data.days || data.days === 0 ? "days" : "day"}{" "}
							<Information information="The number of days in which the event must occur. If you set 0, the execution of this node will happen earliest 10 minutes after the previous one. " />
						</span>{" "}
					</>
				) : (
					<span className="text-text-meta"></span>
				)}
			</div>

			<div className="absolute w-auto top-0 right-0 flex items-center justify-center gap-1 px-1 py-1">
				<Icon
					className="w-4 h-4  text-status-success bg-status-success-light rounded-full p-1"
					icon={faCheck}
				/>

				{data.failed && <StepFailureNote failureCategory={data.failureCategory} />}

				{data.alreadySent && !data.failed && (
					<div className="text-text-meta text-xs">Invite removed</div>
				)}

				{data.deleteNode && !data.alreadySent && (
					<div
						className="flex items-center justify-center cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();

							data.deleteNode?.(props.id);
						}}
					>
						<Icon className="w-3 h-3 text-text-meta" icon={faTrash} />
					</div>
				)}
			</div>

			<div className="z-[100]">
				<Handle
					position={Position.Top}
					style={
						data.isDisconnectedTarget
							? {
									visibility: "visible",
									width: 12,
									height: 12,
									background: "#ef4444",
									border: "2px solid #fff",
									top: -6,
								}
							: {}
					}
					type="target"
				/>
				<Handle
					id="normalEdge"
					position={Position.Bottom}
					style={
						data.hasOpenSource
							? {
									visibility: "visible",
									width: 12,
									height: 12,
									background: "#ef4444",
									border: "2px solid #fff",
									bottom: -6,
								}
							: {}
					}
					type="source"
				/>
			</div>
			{!data.alreadySent && !data.scheduledFor && data?.handleAddStep && (
				<div
					className={classNames(
						"absolute bottom-[-75px] left-[50%] translate-x-[-50%] h-[73px] w-[1px] bg-text-meta opacity-50 z-[90]",
						hasNextNode ? "bg-text-disabled hidden" : "bg-text-meta block"
					)}
				></div>
			)}

			{!data.alreadySent && !data.scheduledFor && data?.handleAddStep && (
				<div
					className="bg-white shadow-md border border-border-light w-6 h-6 text-text-disabled p-2 rounded-full absolute bottom-[-75px] left-[50%] translate-x-[-50%] flex items-center justify-center z-[100]"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();

						data?.handleAddStep?.({
							position: { x: props.positionAbsoluteX, y: props.positionAbsoluteY },
							initiatorId: props.id,
							edgeType: "normalEdge",
							nodeType: "linkedinRemoveInviteNode",
							hasNextNode: hasNextNode,
						});
					}}
				>
					<Icon className="w-4 h-4" icon={faPlus} />
				</div>
			)}
		</div>
	);
};
